import { useState, useEffect } from 'react';
import axios from 'axios';
import { X, Search, Check, Edit2, Save, XCircle } from 'lucide-react';

const API_BASE = import.meta.env.VITE_API_URL || '/api';

export default function LeadSelector({ campaignId, instantlyCampaignId, onClose, onImport }) {
  const [leads, setLeads] = useState([]);
  const [filteredLeads, setFilteredLeads] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState(new Set());
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [editingLead, setEditingLead] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [selectAll, setSelectAll] = useState(false);

  useEffect(() => {
    fetchLeads();
  }, [campaignId]);

  useEffect(() => {
    // Filter leads based on search term
    if (!searchTerm.trim()) {
      setFilteredLeads(leads);
    } else {
      const term = searchTerm.toLowerCase();
      setFilteredLeads(
        leads.filter(
          lead =>
            lead.email?.toLowerCase().includes(term) ||
            lead.first_name?.toLowerCase().includes(term) ||
            lead.last_name?.toLowerCase().includes(term) ||
            lead.company_name?.toLowerCase().includes(term)
        )
      );
    }
  }, [searchTerm, leads]);

  useEffect(() => {
    // Update select all checkbox
    if (filteredLeads.length > 0) {
      const allSelected = filteredLeads.every(lead => selectedLeads.has(lead.id));
      setSelectAll(allSelected);
    } else {
      setSelectAll(false);
    }
  }, [selectedLeads, filteredLeads]);

  const fetchLeads = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE}/leads?limit=1000`);
      setLeads(response.data.leads || []);
      setFilteredLeads(response.data.leads || []);
    } catch (error) {
      console.error('Error fetching leads:', error);
      alert('Error fetching leads');
    } finally {
      setLoading(false);
    }
  };

  const handleSelectLead = (leadId) => {
    const newSelected = new Set(selectedLeads);
    if (newSelected.has(leadId)) {
      newSelected.delete(leadId);
    } else {
      newSelected.add(leadId);
    }
    setSelectedLeads(newSelected);
  };

  const handleSelectAll = () => {
    if (selectAll) {
      // Deselect all filtered leads
      const newSelected = new Set(selectedLeads);
      filteredLeads.forEach(lead => newSelected.delete(lead.id));
      setSelectedLeads(newSelected);
    } else {
      // Select all filtered leads
      const newSelected = new Set(selectedLeads);
      filteredLeads.forEach(lead => newSelected.add(lead.id));
      setSelectedLeads(newSelected);
    }
  };

  const handleEditLead = (lead) => {
    setEditingLead(lead.id);
    setEditForm({
      first_name: lead.first_name || '',
      last_name: lead.last_name || '',
      email: lead.email || '',
      company_name: lead.company_name || '',
      job_title: lead.job_title || '',
      phone: lead.phone || ''
    });
  };

  const handleSaveEdit = async (leadId) => {
    try {
      await axios.put(`${API_BASE}/leads/${leadId}`, editForm);
      // Update the lead in the list
      setLeads(leads.map(lead => 
        lead.id === leadId ? { ...lead, ...editForm } : lead
      ));
      setEditingLead(null);
      setEditForm({});
    } catch (error) {
      console.error('Error updating lead:', error);
      alert('Error updating lead');
    }
  };

  const handleCancelEdit = () => {
    setEditingLead(null);
    setEditForm({});
  };

  const handleBulkEdit = async (field, value) => {
    if (!window.confirm(`Update ${field} to "${value}" for all selected leads?`)) {
      return;
    }

    try {
      const leadIds = Array.from(selectedLeads);
      const updates = leadIds.map(leadId => 
        axios.put(`${API_BASE}/leads/${leadId}`, { [field]: value })
      );
      
      await Promise.all(updates);
      
      // Update leads in the list
      setLeads(leads.map(lead => 
        selectedLeads.has(lead.id) ? { ...lead, [field]: value } : lead
      ));
      
      alert(`Updated ${field} for ${leadIds.length} leads`);
    } catch (error) {
      console.error('Error bulk updating leads:', error);
      alert('Error bulk updating leads');
    }
  };

  const handleImportSelected = async () => {
    if (selectedLeads.size === 0) {
      alert('Please select at least one lead to import');
      return;
    }

    const selectedLeadsData = leads.filter(lead => selectedLeads.has(lead.id));
    
    try {
      await axios.post(`${API_BASE}/campaigns/${campaignId}/import-leads`, {
        lead_ids: Array.from(selectedLeads),
        instantly_campaign_id: instantlyCampaignId
      });
      
      if (onImport) {
        onImport(selectedLeadsData.length);
      }
      
      alert(`Successfully imported ${selectedLeadsData.length} leads to Instantly!`);
      onClose();
    } catch (error) {
      console.error('Error importing leads:', error);
      alert(error.response?.data?.message || 'Error importing leads');
    }
  };

  if (loading) {
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg p-8">
          <div className="text-center">Loading leads...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-6xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex justify-between items-center p-6 border-b">
          <h2 className="text-2xl font-bold text-gray-900">Select Leads to Import</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full"
          >
            <X size={24} />
          </button>
        </div>

        {/* Toolbar */}
        <div className="p-4 border-b bg-gray-50">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center gap-4">
              <div className="relative flex-1 max-w-md">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Search leads..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div className="text-sm text-gray-600">
                {selectedLeads.size} of {filteredLeads.length} selected
              </div>
            </div>
            {selectedLeads.size > 0 && (
              <div className="flex items-center gap-2">
                <button
                  onClick={() => {
                    const field = prompt('Enter field name (first_name, last_name, company_name, job_title, phone):');
                    if (field && ['first_name', 'last_name', 'company_name', 'job_title', 'phone'].includes(field)) {
                      const value = prompt(`Enter new value for ${field}:`);
                      if (value !== null) {
                        handleBulkEdit(field, value);
                      }
                    }
                  }}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 flex items-center gap-2"
                >
                  <Edit2 size={16} />
                  Bulk Edit
                </button>
                <button
                  onClick={handleImportSelected}
                  className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 flex items-center gap-2"
                >
                  <Check size={16} />
                  Import Selected ({selectedLeads.size})
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Leads Table */}
        <div className="flex-1 overflow-auto p-4">
          {filteredLeads.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No leads found{searchTerm && ` matching "${searchTerm}"`}
            </div>
          ) : (
            <table className="w-full">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left">
                    <input
                      type="checkbox"
                      checked={selectAll}
                      onChange={handleSelectAll}
                      className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                    />
                  </th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Email</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">First Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Last Name</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Company</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Job Title</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Phone</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {filteredLeads.map((lead) => (
                  <tr key={lead.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3">
                      <input
                        type="checkbox"
                        checked={selectedLeads.has(lead.id)}
                        onChange={() => handleSelectLead(lead.id)}
                        className="rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                      />
                    </td>
                    {editingLead === lead.id ? (
                      <>
                        <td className="px-4 py-3">
                          <input
                            type="email"
                            value={editForm.email}
                            onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editForm.first_name}
                            onChange={(e) => setEditForm({ ...editForm, first_name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editForm.last_name}
                            onChange={(e) => setEditForm({ ...editForm, last_name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editForm.company_name}
                            onChange={(e) => setEditForm({ ...editForm, company_name: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editForm.job_title}
                            onChange={(e) => setEditForm({ ...editForm, job_title: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <input
                            type="text"
                            value={editForm.phone}
                            onChange={(e) => setEditForm({ ...editForm, phone: e.target.value })}
                            className="w-full px-2 py-1 border border-gray-300 rounded text-sm"
                          />
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex gap-2">
                            <button
                              onClick={() => handleSaveEdit(lead.id)}
                              className="p-1 text-green-600 hover:bg-green-50 rounded"
                            >
                              <Save size={16} />
                            </button>
                            <button
                              onClick={handleCancelEdit}
                              className="p-1 text-red-600 hover:bg-red-50 rounded"
                            >
                              <XCircle size={16} />
                            </button>
                          </div>
                        </td>
                      </>
                    ) : (
                      <>
                        <td className="px-4 py-3 text-sm">{lead.email || '-'}</td>
                        <td className="px-4 py-3 text-sm">{lead.first_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{lead.last_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{lead.company_name || '-'}</td>
                        <td className="px-4 py-3 text-sm">{lead.job_title || '-'}</td>
                        <td className="px-4 py-3 text-sm">{lead.phone || '-'}</td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => handleEditLead(lead)}
                            className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Edit2 size={16} />
                          </button>
                        </td>
                      </>
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t bg-gray-50 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            Showing {filteredLeads.length} of {leads.length} leads
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Cancel
            </button>
            {selectedLeads.size > 0 && (
              <button
                onClick={handleImportSelected}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Import {selectedLeads.size} Lead{selectedLeads.size !== 1 ? 's' : ''}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
